import type { User } from "firebase/auth";
import { loadGameArtForUser } from "@/shadow/merge-game-art";
import {
  defenderShadowToGameArt,
  parsePublicEquippedShadow,
} from "@/shadow/public-equipped-shadow";
import { equippedShadow, loadShadowState } from "@/shadow/firestore-storage";
import { shadowEquippedStatBonus } from "@/shadow/shadow-stat-bonus";
import { equipmentStatBonus, loadEquipmentState } from "@/character/equipment/storage";
import { loadCharacterProfile } from "@/character/storage";
import {
  callCompleteRaid,
  callGetRaidStatus,
  callStartRaid,
  callSurrenderRaid,
  callSyncHomeStructureDefense,
  type RaidPointsPayload,
  type StartRaidResponse,
} from "@/cloud/game-callables";
import { findSolidPlacementId } from "@/home/home-structure-stats";
import { HomeEvents, type HomeStructureAssaultEndedPayload } from "@/home/home-events";
import { syncPlayerPublicStats } from "@/cloud/sync-player-stats";
import {
  applyDungeonBattleRewards,
  computeDungeonBattleRewards,
} from "@/dungeon/dungeon-battle-rewards";
import { showDungeonBattleResult } from "@/dungeon/dungeon-battle-result-panel";
import {
  hasDungeonRaidFloorClear,
  markDungeonRaidFloorClear,
} from "@/dungeon/dungeon-raid-clear-storage";
import { bindDungeonBattleSurrender } from "@/dungeon/dungeon-battle-surrender";
import type { DungeonFloorPublishSnapshot } from "@/dungeon/dungeon-publish";
import { buildDungeonFloorBattlePlan } from "@/dungeon/dungeon-floor-runtime";
import { parseRaidHomeRoom } from "@/dungeon/raid-home-room";
import { createDefaultHomeEconomy, type HomeEconomy } from "@/home/economy";
import { loadHomeEconomyFromIdb } from "@/home/home-idb";
import { startHomeRoom, startTowerBattle } from "@/game/main";
import { equippedPet, loadPetState } from "@/pet/firestore-storage";
import { buildPetSkillRuntimePayload } from "@/pet/skills/runtime";
import {
  loadCharacterProgression,
  progressionXpDisplay,
  type CharacterProgression,
} from "@/game/model/character-progression";
import {
  characterCombatPower,
  mergeStatBonuses,
  playerCombatMaxHp,
  playerRatings,
} from "@/game/model/character-stats";
import { loadMyRankCosmetics } from "@/rank/load-rank-state";
import {
  mountTowerCombatOverlay,
  towerCombatBarMarkup,
  towerReviveModalMarkup,
  type TowerCombatOverlayHandle,
} from "@/tower/tower-combat-overlay";
import { loadCombatBestKillStreak } from "@/game/model/kill-streak";
import {
  TowerEvents,
  type TowerBattleEndedPayload,
  type TowerFloorClearedPayload,
} from "@/tower/tower-events";
import type Phaser from "phaser";

export type MountRaidPageOptions = {
  asModal?: boolean;
  onRequestClose?: () => void;
  onBattleActiveChange?: (active: boolean) => void;
};

type RaidPhase = "prepare" | "lobby" | "fighting" | "summary" | "home" | "done";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const RAID_LOBBY_ID = "raid-lobby-panel";
const RAID_GAME_ID = "raid-game-container";

function normalizeSnapshots(raw: unknown[]): DungeonFloorPublishSnapshot[] {
  return raw
    .filter((x) => x && typeof x === "object")
    .map((x) => x as DungeonFloorPublishSnapshot);
}

export async function mountRaidPage(
  root: HTMLElement,
  user: User,
  options?: MountRaidPageOptions,
): Promise<() => void> {
  const profile = loadCharacterProfile(user.uid);
  if (!profile) {
    window.location.replace("/character/create");
    return () => {};
  }

  let progression: CharacterProgression = loadCharacterProgression(user.uid);
  let rankCosmetics = await loadMyRankCosmetics();
  const [shadowState, petState, economyFromIdb] = await Promise.all([
    loadShadowState(user.uid),
    loadPetState(user.uid),
    loadHomeEconomyFromIdb(user.uid),
  ]);
  const economy: HomeEconomy = economyFromIdb ?? createDefaultHomeEconomy();

  let raid: StartRaidResponse | null = null;
  let raidPointsBudget: RaidPointsPayload | null = null;
  let floorIndex = 0;
  let phase: RaidPhase = "prepare";
  let game: Phaser.Game | null = null;
  let status = "กำลังโหลด…";
  let findingTarget = false;

  const equipmentBonus = () =>
    mergeStatBonuses(
      equipmentStatBonus(loadEquipmentState(user.uid)),
      shadowEquippedStatBonus(equippedShadow(shadowState)),
      rankCosmetics.statBonus,
    );

  const attackerRatings = () => playerRatings(progression, equipmentBonus());
  const attackerMaxHp = () => playerCombatMaxHp(attackerRatings());
  const attackerPower = () => characterCombatPower(attackerRatings(), attackerMaxHp());

  const refreshRaidPoints = async (): Promise<RaidPointsPayload | null> => {
    try {
      return await callGetRaidStatus();
    } catch {
      return raidPointsBudget;
    }
  };

  const ensureShell = () => {
    if (root.querySelector(".tower-layout")) return;
    if (options?.asModal) {
      root.className = "page-dungeon-lobby page-raid-host";
    } else {
      root.className = "game-app page-raid page-tower page-raid--modal";
    }
    root.innerHTML = `
      <div class="tower-layout">
        <div class="tower-stage">
          <section id="${RAID_LOBBY_ID}" class="raid-lobby-host" aria-label="โจมตีด่าน"></section>
          <div id="${RAID_GAME_ID}" class="tower-game-container home-game-container" hidden></div>
          ${towerCombatBarMarkup()}
        </div>
      </div>
      ${towerReviveModalMarkup()}
    `;
  };

  ensureShell();
  const combatOverlay: TowerCombatOverlayHandle = await mountTowerCombatOverlay(root, user, {
    isBattleActive: () => phase === "fighting",
    surrenderConfirm: "ยอมแพ้การโจมตี? แต้มโจมตีจะไม่คืน",
    persistEconomy: true,
    enableRevive: true,
  });

  let syncSurrenderUi = () => {};

  const syncBattleChrome = () => {
    const inWorld = phase === "fighting" || phase === "home";
    root.classList.toggle("page-tower--battle", inWorld);
    root.classList.toggle("page-raid--in-world", inWorld);
    options?.onBattleActiveChange?.(inWorld);
    if (options?.asModal) {
      window.dispatchEvent(
        new CustomEvent(HomeEvents.CombatActiveChanged, {
          detail: { active: phase === "fighting" },
        }),
      );
    }
    const container = root.querySelector<HTMLElement>(`#${RAID_GAME_ID}`);
    const lobbyHost = root.querySelector<HTMLElement>(`#${RAID_LOBBY_ID}`);
    if (container) container.hidden = !inWorld;
    if (lobbyHost) lobbyHost.hidden = inWorld;
    syncSurrenderUi();
  };

  const destroyGame = () => {
    game?.destroy(true);
    game = null;
    combatOverlay.onBattleEnd();
    syncBattleChrome();
  };

  const bindLobby = () => {
    const lobby = root.querySelector<HTMLElement>(`#${RAID_LOBBY_ID}`);
    if (!lobby) return;
    lobby.querySelector("#raid-close")?.addEventListener("click", () => {
      if (phase !== "fighting" && phase !== "home") options?.onRequestClose?.();
    });
    lobby.querySelector("#raid-attack")?.addEventListener("click", () => {
      void findRaidTarget();
    });
    lobby.querySelector("#raid-enter-dungeon")?.addEventListener("click", () => {
      void startFloorBattle();
    });
    lobby.querySelector("#raid-start")?.addEventListener("click", () => {
      void startFloorBattle();
    });
    lobby.querySelector("#raid-surrender")?.addEventListener("click", () => {
      void surrender();
    });
  };

  const renderHudStrip = () => {
    const ratings = attackerRatings();
    const maxHp = attackerMaxHp();
    const power = attackerPower();
    const pts = raidPointsBudget?.raidPoints ?? 0;
    const max = raidPointsBudget?.raidPointsMax ?? 20;
    const bestHit = loadCombatBestKillStreak(user.uid);
    return `
      <div class="dungeon-hud-strip" aria-label="สถานะนักโจมตี">
        <div class="dungeon-hud-chip dungeon-hud-chip--power">
          <span class="dungeon-hud-label">พลัง</span>
          <strong>${power.toLocaleString("en-US")}</strong>
        </div>
        <div class="dungeon-hud-chip dungeon-hud-chip--hp">
          <span class="dungeon-hud-label">HP</span>
          <strong>${maxHp.toLocaleString("en-US")}</strong>
        </div>
        <div class="dungeon-hud-chip dungeon-hud-chip--raid">
          <span class="dungeon-hud-label">โจมตี</span>
          <strong>${pts}</strong><span class="dungeon-hud-muted">/${max}</span>
        </div>
      </div>
      <p class="dungeon-frame-lede raid-hud-meta">
        ATK ${ratings.attack.toLocaleString("en-US")} · แม่น ${ratings.hit.toLocaleString("en-US")}
        · Hit สูงสุด ${bestHit.toLocaleString("en-US")}
      </p>`;
  };

  const renderLobby = () => {
    ensureShell();
    const lobby = root.querySelector<HTMLElement>(`#${RAID_LOBBY_ID}`);
    if (!lobby) return;

    if (phase === "fighting") {
      syncBattleChrome();
      return;
    }

    const atHome = phase === "home";
    const inRun = Boolean(raid) && (phase === "lobby" || atHome);
    const floor = raid ? `${Math.min(floorIndex + 1, raid.maxFloors)} / ${raid.maxFloors}` : "—";
    const pts = raidPointsBudget?.raidPoints ?? 0;
    const canAttack =
      !findingTarget && !inRun && phase !== "done" && pts > 0 && phase === "prepare";

    const xp = progressionXpDisplay(progression);
    const closeBtn = options?.asModal
      ? `<button type="button" class="dungeon-frame-close" id="raid-close" aria-label="ปิด">×</button>`
      : "";

    const canEnterDungeon = Boolean(raid) && phase === "lobby" && !atHome;
    const defenderBlock = raid
      ? `
          <section class="dungeon-floor-list" aria-labelledby="raid-target-title">
            <h2 id="raid-target-title" class="dungeon-section-title">เป้าหมาย</h2>
            <ul class="dungeon-floor-rows">
              <li class="dungeon-floor-row">
                ${
                  canEnterDungeon
                    ? `<button type="button" class="dungeon-floor-main dungeon-floor-main--highlight" id="raid-enter-dungeon">
                  <span class="dungeon-floor-badge" aria-hidden="true">⚔</span>
                  <span class="dungeon-floor-text">
                    <span class="dungeon-floor-name">${escapeHtml(raid.defenderName)}</span>
                    <span class="dungeon-floor-sub">${raid.maxFloors} ชั้น · คะแนนรบ ${(raid.defenderRaidRankPoints ?? 0).toLocaleString("en-US")}</span>
                    <span class="dungeon-floor-sub">เข้าด่านชั้น ${Math.min(floorIndex + 1, raid.maxFloors)} · ตีมอนสเตอร์ในฉาก</span>
                  </span>
                  <span class="dungeon-floor-chevron" aria-hidden="true">›</span>
                </button>`
                    : `<div class="dungeon-floor-main dungeon-floor-main--highlight dungeon-floor-main--static">
                  <span class="dungeon-floor-badge" aria-hidden="true">⚔</span>
                  <span class="dungeon-floor-text">
                    <span class="dungeon-floor-name">${escapeHtml(raid.defenderName)}</span>
                    <span class="dungeon-floor-sub">${raid.maxFloors} ชั้น · คะแนนรบ ${(raid.defenderRaidRankPoints ?? 0).toLocaleString("en-US")}</span>
                    <span class="dungeon-floor-sub">ชั้นที่ต่อสู้: ${floor}</span>
                  </span>
                </div>`
                }
              </li>
            </ul>
          </section>`
      : "";

    const primaryCta =
      phase === "prepare" || phase === "done"
        ? `<button type="button" class="dungeon-publish-save dungeon-publish-save--lg" id="raid-attack" ${canAttack ? "" : "disabled"}>
            ${findingTarget ? "กำลังสุ่ม…" : "⚔ โจมตี"}
          </button>`
        : atHome
          ? `<p class="hint raid-home-assault-hint">ตี <strong>บ้าน / โครงสร้าง</strong> ในฉากจน HP = 0 เพื่อเคลียร์และได้คะแนนรบ</p>`
          : inRun
            ? `<button type="button" class="dungeon-publish-save dungeon-publish-save--lg" id="raid-start">
                ${phase === "lobby" ? "เริ่มด่าน" : "ชั้นถัดไป"}
              </button>`
            : "";

    const surrenderBtn = atHome
      ? `<button type="button" class="dungeon-publish-save dungeon-publish-save--lg dungeon-publish-save--ghost" id="raid-surrender">ยอมแพ้</button>`
      : "";

    lobby.className = "raid-lobby-host";
    lobby.innerHTML = `
      <main class="dungeon-lobby dungeon-lobby--game">
        <div class="dungeon-frame game-system-frame">
          <header class="dungeon-frame-head">
            <div class="dungeon-frame-title-wrap">
              <p class="dungeon-frame-kicker">สุ่มโจมตี</p>
              <h1 class="dungeon-frame-title">โจมตีด่าน</h1>
            </div>
            ${closeBtn}
          </header>

          <p class="dungeon-frame-lede">สุ่มด่านที่ผู้เล่นเปิดเผย · ใช้แต้มโจมตีต่อรัน</p>

          ${renderHudStrip()}

          <section class="dungeon-floor-list" aria-labelledby="raid-attacker-title">
            <h2 id="raid-attacker-title" class="dungeon-section-title">นักโจมตี</h2>
            <ul class="dungeon-floor-rows">
              <li class="dungeon-floor-row">
                <div class="dungeon-floor-main dungeon-floor-main--static">
                  <span class="dungeon-floor-badge" aria-hidden="true">${progression.level}</span>
                  <span class="dungeon-floor-text">
                    <span class="dungeon-floor-name">${escapeHtml(profile.name)}</span>
                    <span class="dungeon-floor-sub">Lv.${progression.level} · EXP ${xp.totalExp.toLocaleString("en-US")}</span>
                  </span>
                </div>
              </li>
            </ul>
          </section>

          ${defenderBlock}

          <p class="dungeon-status hint" id="raid-status" role="status">${escapeHtml(status)}</p>

          <section class="dungeon-publish-panel dungeon-action-panel" aria-label="การดำเนินการ">
            ${primaryCta}
            ${surrenderBtn}
          </section>

          <footer class="dungeon-frame-foot">
            <a class="dungeon-foot-link" href="/home?rank=1">อันดับโลก</a>
          </footer>
        </div>
      </main>
    `;
    bindLobby();
    syncBattleChrome();
  };

  const findRaidTarget = async () => {
    if (findingTarget || raid) return;
    findingTarget = true;
    status = "กำลังสุ่มด่านจากผู้เล่นอื่น…";
    renderLobby();
    try {
      raid = await callStartRaid();
      raidPointsBudget = raid.raidPoints;
      floorIndex = 0;
      phase = "lobby";
      status = `เจอเป้าหมาย · กดเริ่มด่านเพื่อเข้าต่อสู้`;
      findingTarget = false;
      renderLobby();
      return;
    } catch (err) {
      raid = null;
      phase = "prepare";
      status = err instanceof Error ? err.message : "เริ่มรบไม่สำเร็จ";
      raidPointsBudget = await refreshRaidPoints();
    } finally {
      findingTarget = false;
      renderLobby();
    }
  };

  const resetToPrepare = async () => {
    raid = null;
    floorIndex = 0;
    phase = "prepare";
    raidPointsBudget = await refreshRaidPoints();
  };

  const mountGameInContainer = (boot: () => Phaser.Game) => {
    const container = gameHost();
    container.hidden = false;
    container.innerHTML = "";
    game?.destroy(true);
    game = boot();
    syncBattleChrome();
  };

  const startFloorBattle = async () => {
    if (!raid || phase === "home") return;
    const snapshots = normalizeSnapshots(raid.floors);
    const snap = snapshots[floorIndex];
    if (!snap) {
      void enterHomeVisit();
      return;
    }
    phase = "fighting";
    status = `ต่อสู้ด่าน ${snap.floor} ของ ${raid.defenderName}`;
    syncBattleChrome();
    const plan = buildDungeonFloorBattlePlan(snap);
    const art = await loadGameArtForUser(user.uid, profile);
    const activePet = equippedPet(petState);
    combatOverlay.onBattleStart();
    mountGameInContainer(() =>
      startTowerBattle(gameHost(), art, {
        towerFloor: snap.floor,
        progression,
        equipmentStatBonus: equipmentBonus(),
        towerBattlePlan: plan,
        skillRuntime: combatOverlay.getSkillRuntime(),
        rankBadgeBoards: rankCosmetics.badgeBoards,
        rankBadgeConfigs: rankCosmetics.badgeConfigs,
        equippedPet: activePet,
        petSkillRuntime: activePet
          ? buildPetSkillRuntimePayload(activePet.skillBar, economy)
          : undefined,
      }),
    );
  };

  function gameHost(): HTMLElement {
    const c = root.querySelector<HTMLElement>(`#${RAID_GAME_ID}`);
    if (!c) throw new Error("missing raid container");
    return c;
  }

  const enterHomeVisit = async () => {
    if (!raid) return;
    destroyGame();
    phase = "home";
    status = `โจมตีบ้านของ ${raid.defenderName} — ตีโครงสร้างให้แตก`;
    const homeRoom = parseRaidHomeRoom(raid.homeRoom);
    if (!homeRoom) {
      status = "บ้านเป้าโหลดไม่ได้";
      renderLobby();
      return;
    }
    const solidId = findSolidPlacementId(homeRoom);
    if (!solidId) {
      status = "เป้าหมายไม่มีชิ้นบ้านในฉาก — ยอมแพ้หรือจบรัน";
      renderLobby();
      return;
    }
    let defense = {
      maxHp: 100,
      defense: 10,
      currentHp: 100,
      brokenUntil: 0,
    };
    try {
      const synced = await callSyncHomeStructureDefense({ defenderUid: raid.defenderUid });
      defense = {
        maxHp: synced.maxHp,
        defense: synced.defense,
        currentHp: synced.currentHp,
        brokenUntil: synced.brokenUntil,
      };
    } catch {
      /* local defaults */
    }
    if (defense.currentHp <= 0 || defense.brokenUntil > Date.now()) {
      status = "บ้านเป้าอยู่ในช่วงฟื้นฟู · ไม่รับโจมตี";
      renderLobby();
      return;
    }
    renderLobby();
    syncBattleChrome();
    const art = {
      ...(await loadGameArtForUser(user.uid, profile)),
      ...defenderShadowToGameArt(
        parsePublicEquippedShadow(raid?.defenderEquippedShadow ?? null),
      ),
    };
    const runId = raid.runId;
    const defenderUid = raid.defenderUid;
    const onAssaultEnded = (event: Event) => {
      const detail = (event as CustomEvent<HomeStructureAssaultEndedPayload>).detail;
      window.removeEventListener(HomeEvents.StructureAssaultEnded, onAssaultEnded);
      void (async () => {
        if (detail.destroyed) {
          await finishRaid();
          return;
        }
        status = `ทำดาเมจบ้าน +${detail.damageThisRun.toLocaleString("en-US")} · บ้านยังไม่แตก`;
        destroyGame();
        phase = "lobby";
        renderLobby();
      })();
    };
    window.addEventListener(HomeEvents.StructureAssaultEnded, onAssaultEnded);
    const activePet = equippedPet(petState);
    mountGameInContainer(() =>
      startHomeRoom(gameHost(), art, {
        homeData: homeRoom,
        progression,
        equipmentStatBonus: equipmentBonus(),
        rankBadgeBoards: rankCosmetics.badgeBoards,
        rankBadgeConfigs: rankCosmetics.badgeConfigs,
        equippedPet: activePet,
        petSkillRuntime: activePet
          ? buildPetSkillRuntimePayload(activePet.skillBar, economy)
          : undefined,
        homeStructureOwnerUid: user.uid,
        homeStructureCombat: {
          maxHp: defense.maxHp,
          defense: defense.defense,
          currentHp: defense.currentHp,
          solidPlacementId: solidId,
          raidMode: true,
          allowAttack: defenderUid !== user.uid,
          defenderUid,
        },
      }),
    );
    void import("@/cloud/game-callables").then(({ callCommitRaidHomeStructureDamage }) => {
      const bridge = (event: Event) => {
        const detail = (event as CustomEvent<HomeStructureAssaultEndedPayload>).detail;
        void callCommitRaidHomeStructureDamage({
          runId,
          defenderUid,
          damageThisRun: detail.damageThisRun,
        }).catch(() => {});
      };
      window.addEventListener(HomeEvents.StructureAssaultEnded, bridge, { once: true });
    });
  };

  const finishRaid = async () => {
    if (!raid) return;
    destroyGame();
    phase = "done";
    try {
      const res = await callCompleteRaid(raid.runId);
      status = `เคลียร์ถึงบ้านเป้า! +${res.pointsGain} คะแนนรบ`;
      void syncPlayerPublicStats(user);
    } catch (err) {
      status = err instanceof Error ? err.message : "บันทึกผลไม่สำเร็จ";
    }
    await resetToPrepare();
    renderLobby();
  };

  const surrender = async () => {
    if (!raid) return;
    window.dispatchEvent(new CustomEvent(TowerEvents.BattleGiveUp));
    destroyGame();
    phase = "done";
    try {
      await callSurrenderRaid(raid.runId);
      status = "ยอมแพ้ · ไม่ได้คะแนนรบ";
    } catch {
      status = "ยอมแพ้แล้ว";
    }
    await resetToPrepare();
    renderLobby();
  };

  const surrenderControl = bindDungeonBattleSurrender(root, {
    isBattleActive: () => phase === "fighting",
    confirmMessage: "ยอมแพ้การโจมตี? แต้มโจมตีจะไม่คืน",
    onConfirm: () => {
      void surrender();
    },
  });
  syncSurrenderUi = surrenderControl.sync;
  syncSurrenderUi();

  const resultHost = (): HTMLElement =>
    root.querySelector<HTMLElement>(".tower-stage") ?? root;

  const onFloorCleared = (event: Event) => {
    if (!raid || phase !== "fighting") return;
    const detail = (event as CustomEvent<TowerFloorClearedPayload>).detail;
    destroyGame();
    const snapshots = normalizeSnapshots(raid.floors);
    const hasMore = floorIndex + 1 < snapshots.length;
    phase = "summary";
    syncBattleChrome();
    void (async () => {
      const repeatClear = hasDungeonRaidFloorClear(
        user.uid,
        raid!.defenderUid,
        detail.floor,
      );
      const rewards = computeDungeonBattleRewards({
        baseXp: detail.baseXp,
        trial: false,
        repeatClear,
      });
      await showDungeonBattleResult(resultHost(), {
        floor: detail.floor,
        victory: true,
        trial: false,
        baseXp: detail.baseXp,
        monstersKilled: detail.enemiesKilled ?? 0,
        rewards,
        primaryLabel: hasMore ? "ด่านถัดไป" : "ไปบ้านป้องกัน",
      });
      if (!raid) return;
      await applyDungeonBattleRewards(user.uid, rewards);
      if (!repeatClear && (rewards.exp > 0 || rewards.gold > 0)) {
        markDungeonRaidFloorClear(user.uid, raid.defenderUid, detail.floor);
      }
      progression = loadCharacterProgression(user.uid);
      floorIndex += 1;
      if (floorIndex >= snapshots.length) {
        void enterHomeVisit();
        return;
      }
      status = `ผ่านชั้นแล้ว · เข้าชั้น ${floorIndex + 1}…`;
      phase = "lobby";
      renderLobby();
      void startFloorBattle();
    })();
  };

  const onBattleEnded = (event: Event) => {
    const detail = (event as CustomEvent<TowerBattleEndedPayload>).detail;
    if (detail.victory || phase !== "fighting") return;
    status = "แพ้ในด่าน…";
    void surrender();
  };

  window.addEventListener(TowerEvents.FloorCleared, onFloorCleared);
  window.addEventListener(TowerEvents.BattleEnded, onBattleEnded);

  raidPointsBudget = await refreshRaidPoints();
  const ptsLeft = raidPointsBudget?.raidPoints ?? 0;
  status =
    ptsLeft > 0
      ? "กดโจมตีเพื่อสุ่มด่านที่ผู้เล่นอื่นเผยแพร่"
      : "แต้มโจมตีไม่พอ — รอฟื้นหรือใช้ไอเทม";
  renderLobby();

  return () => {
    surrenderControl.unbind();
    window.removeEventListener(TowerEvents.FloorCleared, onFloorCleared);
    window.removeEventListener(TowerEvents.BattleEnded, onBattleEnded);
    destroyGame();
    combatOverlay.destroy();
    options?.onBattleActiveChange?.(false);
    if (options?.asModal) {
      window.dispatchEvent(
        new CustomEvent(HomeEvents.CombatActiveChanged, { detail: { active: false } }),
      );
    }
    root.classList.remove("page-tower--battle");
  };
}
